import { type AxiosInstance } from '@b2ag/axios-client'
import type { CustomerApiClient } from '@b2ag/module-universcoop-sdk'
import { type ApproBffSDK } from '@invivodf/module-appro-bff-sdk'
import {
  type ClientOrder,
  type PayCheckoutQuery,
  PaymentMethodKind,
  PaymentProvider,
  PaymentType,
} from '@invivodf/module-cart-b2ag-interfaces'
import type {
  CheckoutForDelivery,
  Consignment,
  ProductCheckout,
  ProductSelectionsRecord,
} from './checkout-for-delivery.ts'
import type { CheckoutForPayment, CustomerContact } from './checkout-for-payment.ts'
import {
  toAdresses,
  toCheckoutForDelivery,
  toConsignmentsForRequest,
  toDeliveryOptions,
  toProductsCheckout,
  toSetCheckoutLineItemsDeliveryOptions,
} from './checkout-for-delivery.mapper'
import { removeCheckoutToCancelId } from '../checkout-to-cancel-handler'
import { toCheckoutForPayment, toCustomerContacts } from './checkout-for-payment.mapper'

export type Order = {
  id: string
  shouldBypassAuthorization: boolean
  codeExpirationDelayInMinutes?: number
  linkExpirationDelayInMinutes?: number
}

export class CheckoutV2Service {
  constructor(
    private readonly checkoutSdk: ApproBffSDK,
    private readonly checkoutApi: AxiosInstance,
    private readonly customerApiClient: CustomerApiClient,
  ) {}

  async getCustomerContacts(
    partnerId: number,
    memberCode: string,
    hasPhytosanitaryProducts: boolean,
  ): Promise<CustomerContact[]> {
    const result = await this.customerApiClient.getCustomerContacts(partnerId, memberCode)
    return toCustomerContacts(result, hasPhytosanitaryProducts)
  }

  async getCheckoutForDelivery(checkoutId: string): Promise<CheckoutForDelivery | null> {
    const result = await this.checkoutSdk.getCheckoutForDelivery({
      checkoutId,
    })
    if (!result.isSuccess()) {
      return null
    }
    return toCheckoutForDelivery(result.data)
  }

  async getCheckoutForPayment(checkoutId: string): Promise<CheckoutForPayment | null> {
    const result = await this.checkoutSdk.getCheckoutForPayment({
      checkoutId,
    })
    if (!result.isSuccess()) {
      return null
    }

    return toCheckoutForPayment(result.data)
  }

  async cancelCheckout(checkoutId: string) {
    const response = await this.checkoutApi.delete(`/checkouts/${checkoutId}`)
    removeCheckoutToCancelId()
    return response.data
  }

  async getCheckoutConsignmentHomeDeliveryAddresses(checkoutId: string, consignmentId: string, cityCode?: string) {
    const result = await this.checkoutSdk.searchHomeDeliveryAddresses({
      checkoutId,
      consignmentId,
      cityCode: cityCode ?? undefined,
    })
    if (!result.isSuccess()) {
      return null
    }
    return toAdresses(result.data)
  }

  async getCheckoutConsignmentPickupPointDeliveryAddresses(checkoutId: string, consignmentId: string) {
    const result = await this.checkoutSdk.getPickupPointDeliveryAddresses({
      checkoutId,
      consignmentId,
    })
    if (!result.isSuccess()) {
      return null
    }
    return toAdresses(result.data)
  }

  async getCheckoutConsignmentClickAndCollectAddresses(checkoutId: string, consignmentId: string) {
    const result = await this.checkoutSdk.searchClickAndCollectAddresses({
      checkoutId,
      consignmentId,
    })
    if (!result.isSuccess()) {
      return null
    }
    return toAdresses(result.data)
  }

  async searchHomeDeliveryOptions(
    checkoutId: string,
    consignmentId: string,
    addressId: string,
    logisticOfferId: string,
  ) {
    const result = await this.checkoutSdk.searchHomeDeliveryOptions({
      checkoutId,
      consignmentId,
      addressId,
      logisticOfferId,
    })
    if (!result.isSuccess() || !result.data) return null

    return toDeliveryOptions(result.data)
  }

  async searchPickupPointDeliveryOptions(
    checkoutId: string,
    consignmentId: string,
    addressId: string,
    logisticOfferId: string,
  ) {
    const result = await this.checkoutSdk.searchPickupPointDeliveryOptions({
      checkoutId,
      consignmentId,
      addressId,
      logisticOfferId,
    })
    if (!result.isSuccess() || !result.data) return null

    return toDeliveryOptions(result.data)
  }

  async searchClickAndCollectDeliveryOptions(checkoutId: string, consignmentId: string, logisticOfferId: string) {
    const result = await this.checkoutSdk.searchClickAndCollectDeliveryOptions({
      checkoutId,
      consignmentId,
      logisticOfferId,
    })
    if (!result.isSuccess() || !result.data) return null

    return toDeliveryOptions(result.data)
  }

  async setCheckoutDeliveryOptions(
    checkoutId: string,
    consignments: Consignment[],
  ): Promise<CheckoutForPayment | null> {
    const result = await this.checkoutSdk.selectDeliveryOptions({
      checkoutId,
      consignments: toConsignmentsForRequest(consignments),
    })

    if (!result.isSuccess()) {
      return null
    }
    return toCheckoutForPayment(result.data)
  }

  async updateCheckout(
    checkoutId: string,
    props: { confirmation_code?: string; comment?: string },
  ): Promise<ClientOrder> {
    const response = await this.checkoutApi.put(`/checkouts/${checkoutId}`, props)
    return response.data
  }

  async getOrders(checkoutId: string): Promise<ClientOrder> {
    const response = await this.checkoutApi.post<ClientOrder>(`/checkouts/${checkoutId}/orders`)
    return response.data
  }

  async pay(
    checkoutId: string,
    props: {
      kind: PaymentMethodKind
      providerMetadata?: object
    },
    selectedContact?: CustomerContact | null,
    commentForPartner?: string,
  ): Promise<Order> {
    const payCheckoutQuery: PayCheckoutQuery = {
      paymentType: this.getPaymentType(props.kind),
      paymentProvider: this.getProvider(props.kind),
      initPaymentMetadata: props.providerMetadata,
      contactId: selectedContact?.id,
      ...(commentForPartner && { commentForPartner }),
    }
    const response = await this.checkoutSdk.payCheckout({
      checkoutId,
      payment: payCheckoutQuery,
    })

    if (!response.isSuccess()) {
      throw new Error('Failed to pay checkout')
    }

    return response.data
  }

  async regeneratePaymentCode(checkoutId: string): Promise<void> {
    const result = await this.checkoutSdk.regenerateCode({ checkoutId })
    if (!result.isSuccess()) {
      throw new Error('Failed to regenerate payment code')
    }
  }

  async cancelPayment(checkoutId: string): Promise<void> {
    const result = await this.checkoutSdk.cancelPayment({ checkoutId })
    if (!result.isSuccess()) {
      throw new Error('Failed to cancel payment')
    }
  }

  async validatePaymentByCode(checkoutId: string, code: string): Promise<void> {
    const result = await this.checkoutSdk.validateCode({ checkoutId, validationCode: code })
    if (!result.isSuccess()) {
      throw new Error('Failed to validate payment by code')
    }
  }

  async getCheckoutLineItemsForDelivery(checkoutId: string): Promise<ProductCheckout[] | null> {
    const result = await this.checkoutSdk.getCheckoutLineItemsForDelivery({
      checkoutId,
    })
    if (!result.isSuccess()) {
      return null
    }
    return toProductsCheckout(result.data)
  }

  async setCheckoutLineItemsDeliveryOptions(
    checkoutId: string,
    productSelections: ProductSelectionsRecord,
  ): Promise<CheckoutForDelivery | null> {
    const lineItemsDeliveryOptions = toSetCheckoutLineItemsDeliveryOptions(productSelections)
    const result = await this.checkoutSdk.selectCheckoutLineItemsDeliveryOptions({
      checkoutId,
      choices: lineItemsDeliveryOptions,
    })

    if (!result.isSuccess()) {
      return null
    }

    return toCheckoutForDelivery(result.data)
  }

  async cancelConsignment(checkoutId: string, consignmentId: string): Promise<void> {
    const result = await this.checkoutSdk.cancelConsignment({
      checkoutId,
      consignmentId,
    })
    if (!result.isSuccess()) {
      throw new Error('Failed to cancel consignment')
    }
  }

  private getPaymentType(kind: PaymentMethodKind): PaymentType {
    switch (kind) {
      case PaymentMethodKind.BankTransfer:
        return PaymentType.BankTransfer
      case PaymentMethodKind.CardAdyen:
        return PaymentType.Card
      case PaymentMethodKind.CooperativeAccount:
        return PaymentType.Account
      case PaymentMethodKind.Sepa:
        return PaymentType.Sepa
      case PaymentMethodKind.UtaAch:
        return PaymentType.Ach
      case PaymentMethodKind.Cheque:
        return PaymentType.Cheque
      case PaymentMethodKind.Tpe:
        return PaymentType.Tpe
      default:
        throw new Error(`Payment type ${kind} not supported`)
    }
  }

  private getProvider(kind: PaymentMethodKind): PaymentProvider {
    switch (kind) {
      case PaymentMethodKind.BankTransfer:
        return PaymentProvider.IDF_BANK_TRANSFER
      case PaymentMethodKind.CardAdyen:
        return PaymentProvider.ADYEN
      case PaymentMethodKind.CooperativeAccount:
        return PaymentProvider.IDF_COOPERATIVE_ACCOUNT
      case PaymentMethodKind.Sepa:
        return PaymentProvider.IDF_SEPA
      case PaymentMethodKind.UtaAch:
        return PaymentProvider.UTA
      case PaymentMethodKind.Cheque:
        return PaymentProvider.IDF_CHEQUE
      case PaymentMethodKind.Tpe:
        return PaymentProvider.IDF_TPE

      default:
        throw new Error(`Payment provider ${kind} not supported`)
    }
  }
}
